import { createContext, useState, useEffect, useCallback } from "react";
import i18n from "i18next";
import {
  apiGetRequest,
  apiPostRequest,
  apiPutRequest,
} from "../Helpers/Requests";
import endpoints from "../Helpers/Endpoints";
import { createOnboardingAppUrl } from "../Helpers/Actions";
import emitter from "../Utils/events/eventBus";
import { PAGE_VIEWS } from "../Constants";
import { communityRoutes, supplierRoutes } from "../Routes/routePaths";

const normalizePath = (path) => {
  if (!path) return "/";
  return path.replace(/\/+$/, "") || "/";
};

export const AppContext = createContext({});

export const AppProvider = ({ children }) => {
  const [sidebarData, setSidebarData] = useState({});
  const [languages, setLanguages] = useState([]);
  const [isCommunityManager, setisCommunityManager] = useState(false);
  const [isCommunityManagementRole, setisCommunityManagementRole] =
    useState(false);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [showUpdatePopup, setShowUpdatePopup] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [profileData, setProfileData] = useState({});
  const [getStartedContent, setGetStartedContent] = useState({});
  const [userCommunities, setUserCommunities] = useState([]);
  const [systemError, setSystemError] = useState(null); // New state for system errors

  const fetchGetStartedContent = useCallback(async () => {
    try {
      const url = `${endpoints.getStartedTasks}`;
      const response = await apiGetRequest(url);
      setGetStartedContent(response.data);
      return response.data;
    } catch (error) {
      console.error("Failed to fetch get started content:", error);
      return null;
    }
  }, []);

  const fetchUserCommunities = useCallback(async () => {
    try {
      const url = `${endpoints.getUserCommunities}`;
      const response = await apiGetRequest(url);
      const communities = response.data.communities;
      setUserCommunities(communities);
    } catch (error) {
      console.error("Failed to fetch user communities:", error);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const currentRoutePathName = normalizePath(window.location.pathname);

        const isHomeRoute = currentRoutePathName === "/";

        if (!isHomeRoute) {
          const isSupplierRoute = supplierRoutes.some(
            (route) => normalizePath(route.path) === currentRoutePathName,
          );

          const isCommunityManagerRoute = communityRoutes.some(
            (route) => normalizePath(route.path) === currentRoutePathName,
          );

          // /dashboard is registered in BOTH route lists — it is one route
          // rendering whichever dashboard matches the current role. Toggling on
          // a shared route would decide the role FOR the user, and since the
          // supplier branch is tested first a manager who also sells was
          // flipped to SUPPLIER on every visit, making the community-manager
          // dashboard unreachable. Shared routes must leave the role alone.
          const isSharedRoute = isSupplierRoute && isCommunityManagerRoute;

          if (!isSharedRoute && (isSupplierRoute || isCommunityManagerRoute)) {
            const pageView = isSupplierRoute
              ? PAGE_VIEWS.SUPPLIER_MANAGEMENT_VIEW
              : PAGE_VIEWS.COMMUNITY_MANAGEMENT_VIEW;
            await apiPostRequest(endpoints.toggleRoleByPageView, {
              pageView,
            });
          }
        }

        const [sidebarResponse, languagesResponse, profileResponse] =
          await Promise.all([
            apiGetRequest(endpoints.getSidebarData),
            apiGetRequest(endpoints.getAllLanguages),
            apiGetRequest(endpoints.getProfileData),
            fetchGetStartedContent(),
          ]);

        const sidebar = sidebarResponse?.data || {};
        const langs = languagesResponse?.data?.data || [];
        const profile = profileResponse?.data || {};

        setSidebarData(sidebar);
        setLanguages(langs);
        setProfileData(profile);

        const currentLang = langs
          .find((l) => l.name === sidebar.language)
          ?.code?.trim();
        if (currentLang) {
          i18n.changeLanguage(currentLang);
          localStorage.setItem("appLanguage", currentLang);
          document.documentElement.lang = currentLang;
        }

        if (sidebar.last_role_view !== "SUPPLIER") {
          await fetchUserCommunities();
        }

        const isUserAdmin = sidebar?.last_role_view === "COMMUNITY_MANAGER";
        setisCommunityManager(isUserAdmin);
        localStorage.setItem("isCommunityManager", isUserAdmin);

        const isCMRole =
          sidebar?.last_role_view === "COMMUNITY_MANAGER" ||
          sidebar?.last_role_view === "COMMUNITY_MODERATOR";
        setisCommunityManagementRole(isCMRole);
        localStorage.setItem("isCommunityManagementRole", isCMRole);

        if (sidebar.showWelcomePopup) {
          setShowWelcomePopup(true);
        }
        if (sidebar.showUpdatePopup) {
          setShowUpdatePopup(true);
        }
      } catch (error) {
        console.error(error);
        // Check for 401 or 404 errors in any of the Promise.all calls
        if (
          error.response &&
          (error.response.status === 401 || error.response.status === 404)
        ) {
          if (
            error.response &&
            (error.response.status === 401 || error.response.status === 404)
          ) {
            window.location.href = createOnboardingAppUrl({ path: "/welcome" });
          } else if (
            error.response &&
            (error.response.status === 500 || error.response.status === 502)
          ) {
            setSystemError("We are facing issues");
          }
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [fetchGetStartedContent, fetchUserCommunities]);

  const updateLanguage = useCallback(
    async (languageId) => {
      try {
        await apiPutRequest(endpoints.updateLanguage, { languageId });
        const lang = languages.find((l) => l.id === languageId)?.code?.trim();
        if (lang) {
          i18n.changeLanguage(lang);
          localStorage.setItem("appLanguage", lang);
          document.documentElement.lang = lang;
        }
      } catch (error) {
        console.error("Failed to update language:", error);
        // Check for 401 or 404 errors in any of the Promise.all calls
        if (
          error.response &&
          (error.response.status === 401 || error.response.status === 404)
        ) {
          if (
            error.response &&
            (error.response.status === 401 || error.response.status === 404)
          ) {
            window.location.href = createOnboardingAppUrl({ path: "/welcome" });
          } else if (
            error.response &&
            (error.response.status === 500 || error.response.status === 502)
          ) {
            setSystemError("We are facing issues");
          }
        }
      }
    },
    [languages],
  );

  // Re-check for update popup when user returns to the tab
  const checkForUpdatePopup = useCallback(async () => {
    try {
      const response = await apiGetRequest(endpoints.getSidebarData);
      const sidebar = response?.data || {};
      if (sidebar.showUpdatePopup) {
        setShowUpdatePopup(true);
      }
    } catch (error) {
      // Silently fail - not critical
    }
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkForUpdatePopup();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    emitter.on("fetch-get-started-content", fetchGetStartedContent);
    emitter.on("check-update-popup", checkForUpdatePopup);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      emitter.off("fetch-get-started-content", fetchGetStartedContent);
      emitter.off("check-update-popup", checkForUpdatePopup);
    };
  }, [fetchGetStartedContent, checkForUpdatePopup]);

  return (
    <AppContext.Provider
      value={{
        sidebarData,
        isCommunityManager,
        isCommunityManagementRole,
        languages,
        updateLanguage,
        showWelcomePopup,
        setShowWelcomePopup,
        showUpdatePopup,
        setShowUpdatePopup,
        isLoading,
        profileData,
        getStartedContent,
        userCommunities,
        fetchGetStartedContent,
        systemError,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
